import React, { useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import { validate as validateUuid } from 'uuid';
import {
	DndContext,
	closestCenter,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
	DragMoveEvent,
} from '@dnd-kit/core';
import {
	SortableContext,
	useSortable,
	verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

import './session-modules-lists.scss';
import { useTypedSelector } from "../../../../../../store/reducers/use-typed-selector";
import { ModuleGroups, ModuleMap, PageModule, PageModuleGroupModules, PageModuleType, Session, TranslateString } from "../../../../../../types/working-model";
import CollapsingList from "../../../../../general-ui/collapsing-list/collapsing-list";
import { updateSession, updateWorkingSession } from '../../../../../../store/actions/admin/create-event/session';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import BasicCard from '../../../../../general-ui/basic-card/basic-card';
import Switch from '../../../../../general-ui/switch/switch';
import { TogglePageModuleVisibility } from '../../../../../../connection/page-modules';
import BroadcastCard from '../broadcast-card';
import TabLayouts from '../tab-layouts/tab-layouts';
import WaitingIndicator from '../../../../../general-ui/waiting-indicator/waiting-indicator';
import { setSessionGroup } from '../../../../../../store/actions/admin/event-panel';

interface INestedSortableModuleProps {
	moduleId: string;
	module: PageModule;
	id: string;
	moduleGroupUuid?: string;
}

const NestedSortableModule: React.FC<INestedSortableModuleProps> = ({ id, module, moduleGroupUuid }) => {
	const { token, workingSession } = useTypedSelector(state => ({
		workingSession: state.CreateSessionReducer.workingSession,
		token: state.AuthReducer.token,
	}));

	const dispatch = useDispatch();

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
	} = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};


	const handleToggleModuleSwitch = async (module: PageModule, on: boolean) => {
		if (!workingSession || !module.id) return;
		// update redux and database
		const updatedSession: Session = {
			...workingSession,
			modules: workingSession!.modules.map(sessionModule => {
				if (sessionModule.id !== module.id) return sessionModule;
				return {
					...sessionModule,
					is_on: on,
				};
			}),
		};
		dispatch(updateWorkingSession(updatedSession));
		await TogglePageModuleVisibility(token!, module.id, on);
	};

	function scrollToSessionModule() {
		moduleGroupUuid && dispatch(setSessionGroup(moduleGroupUuid));
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
		>
			<div className="nested-module-card-container" onClick={scrollToSessionModule}>
				<BasicCard className="nested-module-card">
					<div className="title">
						<div className="drag-handle" {...attributes} {...listeners}>
							<Icon name={ICONS.DRAG_HANDLE} size={12} color={COLORS.GRAY} />
						</div>
						{ModuleMap[module.type]
							//keeping for now just in case we want the custom translated module names
							// module?.content?.custom_heading?.base?.trim()?.length
							// 	? module.content.custom_heading.base
							// 	: ModuleMap[module.type]
						}
					</div>
					<div>
						<Switch
							value={module.id}
							onClick={(_, on) => handleToggleModuleSwitch(module, on)}
							on={module.is_on}
						/>
					</div>
				</BasicCard>
			</div>
		</div>
	);
};


interface ISortableTopLevelSessionModuleProps {
	moduleGroup: PageModuleGroupModules;
	workingSession: Session;
	id: string;
	shouldCloseAllLists: boolean;
}

const SortableTopLevelSessionModule: React.FC<ISortableTopLevelSessionModuleProps> = ({
	id,
	moduleGroup,
	workingSession,
	shouldCloseAllLists,
}) => {
	const dispatch = useDispatch();
	const token = useTypedSelector(state => state.AuthReducer.token);
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
	} = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};

	const handleToggleTopLevelSwitch = async (
		selectedModuleGroup: PageModuleGroupModules,
		on: boolean
	) => {
		const updatedSession: Session = {
			...workingSession,
			module_grouping: workingSession?.module_grouping?.map(moduleGroup => {
				if (selectedModuleGroup.uuid !== moduleGroup.uuid) return moduleGroup;
				return {
					...moduleGroup,
					is_on: on,
				};
			}),
		};

		batch(() => {
			// update db
			dispatch(updateSession(updatedSession, token!));
			// update redux
			dispatch(updateWorkingSession(updatedSession));
		});
	};

	function scrollToEditor() {
		dispatch(setSessionGroup(moduleGroup.uuid));
	}

	let name: string;
	// This was previously static text, leaving a saftey check for backwards compatibility
	//Check to see if "name" is a Translate string, if so return the translated value, if not return the static text
	const stringName = moduleGroup?.name as ModuleGroups;
	const translateName = moduleGroup?.name as TranslateString;
	if (typeof translateName === 'object') {
		// fallback is for sessions created with a default language that was not one of the session languages (before that got fixed)
		name = translateName.base ?? Object.values(translateName)[0];
	} else {
		name = stringName;
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
		>
			<CollapsingList
				label=""
				closeAll={shouldCloseAllLists}
				overflowVisible={true}
				customHeader={(toggleOpen, isOpen) => (
					<BasicCard p={0}>
						<div className="top-level-card">
							<button className="no-style" onClick={() => {
								toggleOpen();
								scrollToEditor();
							}}>
								<Icon name={isOpen ? ICONS.KEYBOARD_ARROW_DOWN : ICONS.KEYBOARD_ARROW_RIGHT} color={COLORS.WHITE} size={16} />
							</button>
							<div className="top-level-card-content">
								<div className="title">
									<div className="drag-handle" {...attributes} {...listeners}>
										<Icon name={ICONS.DRAG_HANDLE} size={12} color={COLORS.GRAY} />
									</div>
									{name}
								</div>
								<div>
									<Switch
										value={name}
										onClick={(_, on) => handleToggleTopLevelSwitch(moduleGroup, on)}
										on={moduleGroup.is_on}
									/>
								</div>
							</div>
						</div>
					</BasicCard>
				)}
			>
				<SortableContext
					items={moduleGroup.modules?.map((moduleId: number) => moduleId.toString())}
					strategy={verticalListSortingStrategy}
				>
					{moduleGroup.modules.map((moduleId: number) => {
						const module = workingSession.modules.find((module: PageModule) => module.id === moduleId);
						if (!module) return null;
						// FUTURE DEV - hiding quizzes and posts
						if (module.type === PageModuleType.quizzes) return null;
						return (
							<NestedSortableModule
								key={moduleId}
								id={moduleId.toString()}
								moduleId={moduleId.toString()}
								module={module}
								moduleGroupUuid={moduleGroup.uuid}
							/>
						);
					})}
				</SortableContext>
			</CollapsingList>
		</div>

	);
};

const SessionModuleGroupingList: React.FC = () => {

	const { workingSession, token } = useTypedSelector(state => ({
		workingSession: state.CreateSessionReducer.workingSession,
		token: state.AuthReducer.token,
	}));

	const [shouldCloseAllLists, setShouldCloseAllLists] = useState(false);

	const dispatch = useDispatch();

	const sensors = useSensors(
		useSensor(PointerSensor),
	);

	const handleTopLevelDragEnd = (event: DragEndEvent) => {
		setShouldCloseAllLists(false);
		if (validateUuid(event.active.id)) {
			if (workingSession?.module_grouping) {
				const _modules = [...workingSession.module_grouping];
				const startIndex = _modules.findIndex(
					(module: PageModuleGroupModules) => module.uuid === event.active.id
				);
				const newIndex = _modules.findIndex(
					(module: PageModuleGroupModules) => module.uuid === event?.over?.id
				);

				if (startIndex !== newIndex) {
					const [module] = _modules.splice(startIndex, 1);
					_modules.splice(newIndex, 0, module);
					const updatedSession: Session = {
						...workingSession,
						module_grouping: _modules,
					};
					batch(() => {
						// update db
						dispatch(updateSession(updatedSession, token!));
						// update redux
						dispatch(updateWorkingSession(updatedSession));
					});
				}
			}

		} else if (workingSession?.module_grouping) {
			// else is nested sortable modules:
			const activeId = Number(event.active.id);
			const overId = Number(event?.over?.id);
			// first find the module grouping that the activeId belongs to
			// then do the splicing
			// then update the workingSession locally
			// then save in db and redux
			let hasChangedPosition = true;
			const updatedSession: Session = {
				...workingSession,
				module_grouping: workingSession?.module_grouping.map((module: PageModuleGroupModules) => {
					const matched = module.modules.find((moduleId: number) => moduleId === Number(activeId));
					if (!matched) return module;
					const _modules = [...module.modules];
					const startIndex = _modules.findIndex((module: number) => module === activeId);
					const newIndex = _modules.findIndex((module: number) => module === overId);
					if (startIndex === newIndex) {
						hasChangedPosition = false;
						return module;
					}
					const [moduleSplice] = _modules.splice(startIndex, 1);
					_modules.splice(newIndex, 0, moduleSplice);
					return {
						...module,
						modules: _modules,
					};
				}),
			};

			if (hasChangedPosition) {
				batch(() => {
					// update db
					dispatch(updateSession(updatedSession, token!));
					// update redux
					dispatch(updateWorkingSession(updatedSession));
				});
			}
		}
	};

	// if top level card is moved, we should close all lists so it works properly
	const handleDragMove = (event: DragMoveEvent) => {
		if (validateUuid(event.active.id)) {
			if (!shouldCloseAllLists) {
				setShouldCloseAllLists(true);
			}
		}
	};

	if (!workingSession) return null;
	if (!workingSession?.module_grouping) return <WaitingIndicator />;
	return (
		<div className="session-module-grouping-list-container">
			<BroadcastCard />

			<TabLayouts />

			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={handleTopLevelDragEnd}
				modifiers={[restrictToParentElement]}
				onDragMove={handleDragMove}
				autoScroll={false}
			>
				<SortableContext
					items={workingSession?.module_grouping?.map((module: PageModuleGroupModules) => module.uuid)}
					strategy={verticalListSortingStrategy}
				>
					{workingSession.module_grouping.map((moduleGroup: PageModuleGroupModules) => {
						return (
							<SortableTopLevelSessionModule
								key={moduleGroup.uuid}
								id={moduleGroup.uuid}
								workingSession={workingSession}
								moduleGroup={moduleGroup}
								shouldCloseAllLists={shouldCloseAllLists}
							/>
						);
					})}
				</SortableContext>
			</DndContext>
		</div>
	);
};

export default SessionModuleGroupingList;
